import sanitizeHtml from "sanitize-html";
import type { Config as UniqueNamesConfig } from "unique-names-generator";
import { adjectives, animals, uniqueNamesGenerator } from "unique-names-generator";

import type { LayoutLocator, SortablePayload } from "./types";

export const getInitials = (name: string) => {
  // eslint-disable-next-line unicorn/better-regex
  const regex = new RegExp(/(\p{L}{1})\p{L}+/gu);
  const initials = [...name.matchAll(regex)];

  return ((initials.shift()?.[1] ?? "") + (initials.pop()?.[1] ?? "")).toUpperCase();
};

export const isUrl = (string: string | null | undefined) => {
  if (!string) return false;

  const urlRegex = /https?:\/\/[^\n ]+/i;

  return urlRegex.test(string);
};

export const isEmptyString = (string: string) => {
  if (string === "<p></p>") return true;
  return string.trim().length === 0;
};

export const extractUrl = (string: string) => {
  const urlRegex = /https?:\/\/[^\n ]+/i;

  const result = urlRegex.exec(string);
  return result ? result[0] : null;
};

export const generateRandomName = (options?: Omit<UniqueNamesConfig, "dictionaries">) => {
  return uniqueNamesGenerator({
    dictionaries: [adjectives, adjectives, animals],
    style: "capital",
    separator: " ",
    length: 3,
    ...options,
  });
};

export const processUsername = (string?: string | null) => {
  if (!string) return "";

  return string.replace(/[^\d.A-Za-z-]/g, "").toLowerCase();
};

export const parseLayoutLocator = (payload: SortablePayload | null): LayoutLocator => {
  if (!payload) return { page: 0, column: 0, section: 0 };

  const section = payload.index;
  const [page, column] = payload.containerId.split(".").map(Number);

  return { page, column, section };
};

/**
 * Rich-text fields (the top-level summary and every item's "summary") render
 * through a Tiptap editor expecting HTML, but a source of plain prose — an AI
 * extraction that ignored the "use HTML" prompt instruction, or a deterministic
 * parser that only ever produces plain text — needs converting first. If the
 * text already looks like a real list, trust it. Otherwise: a single line
 * becomes one paragraph; multiple lines (the common "one achievement per line"
 * shape) become a bullet list, stripping any bullet marker already present so
 * it isn't doubled up with the list's own marker.
 */
export const normalizeSummaryHtml = (text: string): string => {
  if (!text) return "";
  // A real list is already the shape we want — trust it as-is.
  if (/<(ul|ol)[\s>]/i.test(text)) return text;

  const hasHtml = /<[a-z][\S\s]*>/i.test(text);
  // Strip a single wrapping <p>...</p> so a "<p>A<br>B<br>C</p>" response
  // (real Groq output observed live: the model wrote HTML but ignored the
  // "use <ul><li> for multiple bullets" instruction, using <br> instead)
  // still gets re-split into separate lines below rather than passing
  // through as one unstyled paragraph.
  const unwrapped = text.replace(/^\s*<p[^>]*>([\S\s]*)<\/p>\s*$/i, "$1");
  const lines = unwrapped
    .split(/<br\s*\/?>|\n+/i)
    .map((line) => line.trim())
    .filter(Boolean);

  if (lines.length <= 1) return hasHtml ? text : `<p>${text}</p>`;
  const items = lines.map((line) => `<li>${line.replace(/^[*•-]\s*/, "")}</li>`).join("");
  return `<ul>${items}</ul>`;
};

export const sanitize = (html: string, options?: sanitizeHtml.IOptions) => {
  const allowedTags = (options?.allowedTags ?? []) as string[];

  return sanitizeHtml(html, {
    ...options,
    allowedTags: [
      ...allowedTags,
      "a",
      "abbr",
      "address",
      "article",
      "aside",
      "b",
      "bdi",
      "bdo",
      "blockquote",
      "br",
      "caption",
      "cite",
      "code",
      "col",
      "colgroup",
      "data",
      "dd",
      "dfn",
      "div",
      "dl",
      "dt",
      "em",
      "figcaption",
      "figure",
      "footer",
      "h1",
      "h2",
      "h3",
      "h4",
      "h5",
      "h6",
      "header",
      "hgroup",
      "hr",
      "i",
      "img",
      "kbd",
      "li",
      "main",
      "main",
      "mark",
      "nav",
      "ol",
      "p",
      "pre",
      "q",
      "rb",
      "rp",
      "rt",
      "rtc",
      "ruby",
      "s",
      "samp",
      "section",
      "small",
      "span",
      "strong",
      "sub",
      "sup",
      "table",
      "tbody",
      "td",
      "tfoot",
      "th",
      "thead",
      "time",
      "tr",
      "u",
      "ul",
      "var",
      "wbr",
    ],
    allowedAttributes: {
      ...options?.allowedAttributes,
      "*": ["class", "style"],
      a: ["href", "target", "rel"],
      img: ["src", "alt"],
    },
    transformTags: {
      /* eslint-disable @typescript-eslint/dot-notation -- attribs index sig; TS4111 requires bracket access */
      a: (tagName, attribs) => {
        const out = { ...attribs };
        if (out["target"] === "_blank") {
          out["rel"] = "noopener noreferrer";
        }
        if (out["href"] && /^javascript:/i.test(out["href"])) {
          out["href"] = "";
        }
        return { tagName, attribs: out };
      },
      /* eslint-enable @typescript-eslint/dot-notation */
    },
    allowedStyles: {
      ...options?.allowedStyles,
      "*": { "text-align": [/^left$/, /^right$/, /^center$/, /^justify$/] },
    },
  });
};
